/**
 * Application des erreurs Salesforce sur le dossier affiché.
 *
 * Le serveur rend une liste d'erreurs situées (critère, sous-critère,
 * question). Ces fonctions les déposent sur les questions concernées afin que
 * les composants les affichent, et indiquent où amener le candidat.
 *
 * Les erreurs sont déposées sur l'objet question lui-même (`salesforceError`)
 * plutôt que transmises par une cascade de props : les composants de question
 * reçoivent déjà la question entière via `questionInfo`.
 *
 * Attention : `salesforceError` ne doit jamais être enregistré sur le serveur.
 * C'est un état d'affichage, pas une donnée du dossier — `stripSalesforceErrors`
 * s'en assure avant toute sauvegarde.
 */

/** Retire toutes les erreurs précédemment posées (nouvelle tentative d'envoi). */
export function clearSalesforceErrors(data) {
  if (!data || !Array.isArray(data.data)) return data;

  data.data.forEach((entry) => {
    (entry && entry.criteres ? entry.criteres : []).forEach((critere) => {
      (critere && critere.sousCriteres ? critere.sousCriteres : []).forEach((sousCritere) => {
        (sousCritere && sousCritere.questions ? sousCritere.questions : []).forEach((question) => {
          if (question && question.salesforceError) {
            delete question.salesforceError;
          }
        });
      });
    });
  });

  return data;
}

/**
 * Dépose les erreurs sur les questions visées.
 *
 * @returns {{applied:number, orphans:Array, firstLocation:object|null}}
 *   applied       nombre d'erreurs effectivement posées sur une question
 *   orphans       erreurs sans question identifiable (règles métier du CRM)
 *   firstLocation où amener le candidat, ou null
 */
export function applySalesforceErrors(data, errors, filiereIndex) {
  const orphans = [];
  const blocking = [];
  let applied = 0;
  let firstLocation = null;

  if (!data || !Array.isArray(data.data)) {
    return { applied, orphans: errors || [], firstLocation, blocking };
  }

  const entry = data.data[filiereIndex];
  if (!entry) {
    return { applied, orphans: errors || [], firstLocation, blocking };
  }

  (errors || []).forEach((error) => {
    const hasCoords = Number.isInteger(error.critereIndex)
      && Number.isInteger(error.sousCritereIndex)
      && Number.isInteger(error.questionIndex);

    if (!hasCoords) {
      orphans.push(error);
      return;
    }

    const critere = (entry.criteres || [])[error.critereIndex];
    const sousCritere = critere && (critere.sousCriteres || [])[error.sousCritereIndex];
    const question = sousCritere && (sousCritere.questions || [])[error.questionIndex];

    // Vérification de cohérence : si l'identifiant ne correspond pas, la
    // structure a changé entre l'envoi et la réponse. On préfère signaler
    // l'erreur en tête de page plutôt que de l'afficher sur la mauvaise question.
    if (!question || (error.questionId != null && question.id !== error.questionId)) {
      orphans.push(error);
      return;
    }

    question.salesforceError = {
      message: error.message,
      detail: error.detail || '',
      code: error.code,
      fixable: error.fixable !== false,
      value: error.value
    };
    applied += 1;

    if (error.fixable !== false) {
      if (!firstLocation) {
        firstLocation = {
          critereIndex: error.critereIndex,
          sousCritereIndex: error.sousCritereIndex,
          questionId: question.id
        };
      }
    } else {
      // Erreur localisée mais que le candidat ne peut pas corriger : elle est
      // affichée sur la question ET reprise en tête de page. Sans ce rappel,
      // un refus de ce type ne déclencherait aucune navigation et resterait
      // invisible si c'était la seule erreur.
      blocking.push(error);
    }
  });

  return { applied, orphans, firstLocation, blocking };
}

/**
 * Copie du dossier sans les erreurs d'affichage, pour l'enregistrement.
 * Sans cela, `salesforceError` finirait dans le fichier du dossier.
 */
export function stripSalesforceErrors(data) {
  if (!data) return data;
  const copy = JSON.parse(JSON.stringify(data));
  clearSalesforceErrors(copy);
  return copy;
}

/** Fait défiler l'écran jusqu'à la question fautive. */
export function scrollToQuestion(questionId) {
  if (typeof document === 'undefined' || questionId == null) return;

  // Les composants de question posent id={"id:" + question.id}.
  window.requestAnimationFrame(() => {
    const node = document.getElementById(`id:${questionId}`);
    if (node && typeof node.scrollIntoView === 'function') {
      node.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  });
}
